"use client";

import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { LEDGER_ENTRY_DATE_TIME_POSTINGS_FORMAT } from "@/constants";

interface PostingsDetailsProps {
  entry: LedgerEntryResolvedType;
}

/**
 * Postings ("Other" activity) render base ledger fields only. The BFF's
 * `resolvedData` for this journal (`FilteredPosting`: meta/reference/
 * valueDate) carries nothing this view displays, so an unresolved posting is
 * a COMPLETE render, not a degraded one, and must not show the
 * missing-resolution support alert. In production every postings row did
 * (CUSTOMER-BFF-Z: journal-general rejected the BFF's forwarded token, so
 * `resolvedData` was always null) and sent customers to support for
 * information that was already on screen. Sibling detail components that
 * actually read `resolvedData` keep the alert.
 */
export default function PostingsDetails({ entry }: PostingsDetailsProps) {
  const tLedgerFields = useTranslations("ledger.fields");

  const isCredit = entry.credit > 0;
  const isDebit = entry.debit > 0;

  return (
    <div className="space-y-0">
      {isCredit && (
        <DetailRow
          label={tLedgerFields("amountReceived")}
          value={
            <FormattedCurrencyAmount
              balance={entry.credit}
              product={entry.product}
            />
          }
          translateValue={false}
        />
      )}

      {isDebit && (
        <DetailRow
          label={tLedgerFields("amountDeducted")}
          value={
            <FormattedCurrencyAmount
              balance={entry.debit}
              product={entry.product}
              forceNegative
            />
          }
          translateValue={false}
        />
      )}

      <DetailRow
        label={tLedgerFields("valueDate")}
        value={format(
          new Date(entry.valueDate),
          LEDGER_ENTRY_DATE_TIME_POSTINGS_FORMAT
        )}
      />

      <DetailRow
        label={tLedgerFields("comment")}
        value={entry.comment}
        textClassName="max-w-[300px]"
        translateValue={false}
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        textClassName="font-mono text-sm justify-end flex-1 items-end "
        label={tLedgerFields("referenceId")}
        value={entry.uuid}
        translateValue={false}
      />
    </div>
  );
}
